import React, { FC, useEffect, useState } from 'react';
import { Box, Grid, Typography, CircularProgress, Alert } from '@mui/material';
import clientService from 'src/clientService';
import MediaCard from './MediaCard';
import { useAppState } from 'src/state-management/state-provider';
import MediaViewerDialog from './MediaViewerDialog';

interface MediaGalleryProps {
  projectId: string;
  showActions?: boolean;
}

const MediaGallery: FC<MediaGalleryProps> = ({ projectId, showActions = true }) => {
  const { state, dispatch } = useAppState();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [viewerOpen, setViewerOpen] = useState<boolean>(false);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);

  const projectMedia = state.admin.projectMedia[projectId] || [];

  useEffect(() => {
    const fetchMedia = async () => {
      // Only fetch if the media for this project isn't already in the state
      if (projectMedia.length > 0) {
        return;
      }
      setLoading(true);
      try {
        const response = await clientService.api.getProjectMediaListApiV1AdminProjectsProjectIdMediaGet(projectId);
        dispatch({
          type: 'ADMIN_SET_PROJECT_MEDIA',
          payload: { projectId, media: response.data }
        });
      } catch (err) {
        console.error('Failed to fetch media', err);
        setError('Failed to load existing media.');
      } finally {
        setLoading(false);
      }
    };

    fetchMedia();
  }, [projectId, dispatch, projectMedia.length]);

  const handleOpenViewer = (index: number) => {
    setSelectedIndex(index);
    setViewerOpen(true);
  };

  const handleCloseViewer = () => {
    setViewerOpen(false);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
        <CircularProgress sx={{ color: '#10B981' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert 
        severity="error" 
        sx={{ 
          bgcolor: 'rgba(239, 68, 68, 0.1)', 
          color: '#EF4444', 
          border: '1px solid rgba(239, 68, 68, 0.2)',
          borderRadius: '12px'
        }}
      >
        {error}
      </Alert>
    );
  }

  if (projectMedia.length === 0) {
    return (
      <Box sx={{ p: 6, border: '1px dashed #222', borderRadius: '24px', textAlign: 'center' }}>
        <Typography sx={{ color: '#666' }}>No media has been uploaded for this project yet.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Grid container spacing={2}>
        {projectMedia.map((item, index) => (
          <Grid item key={item.media_id} xs={12} sm={6} md={4} lg={3}>
            <MediaCard 
              mediaItem={item} 
              showActions={showActions} 
              onMediaClick={() => handleOpenViewer(index)}
            />
          </Grid>
        ))}
      </Grid>
      
      <MediaViewerDialog
        open={viewerOpen}
        onClose={handleCloseViewer}
        mediaList={projectMedia}
        initialIndex={selectedIndex}
      />
    </Box>
  );
};

export default MediaGallery;
